import { useMemo, useState } from "react";
import LedgerNumber from "@/components/LedgerNumber";
import { formatINR } from "@/lib/brand";
import YearBreakdown from "./YearBreakdown";
import CalculatorActions from "./CalculatorActions";

// FV of SIP (invested at start of each month): FV = P × [((1+r)^n − 1)/r] × (1+r)
function sipFV(P, monthlyRate, n) {
  if (P <= 0 || n <= 0) return 0;
  if (monthlyRate === 0) return P * n;
  return (P * (Math.pow(1 + monthlyRate, n) - 1) / monthlyRate) * (1 + monthlyRate);
}

export default function SipCalculator() {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(15);

  const { fv, invested, gains, investedPct, gainsPct, rows } = useMemo(() => {
    const P = Number(monthly) || 0;
    const r = (Number(rate) || 0) / 12 / 100;
    const yrs = Number(years) || 0;
    const n = yrs * 12;
    const fv = sipFV(P, r, n);
    const invested = P * n;
    const gains = fv - invested;
    const investedPct = fv > 0 ? (invested / fv) * 100 : 0;
    const gainsPct = 100 - investedPct;
    const rows = [];
    for (let k = 1; k <= yrs; k++) {
      const nk = k * 12;
      const fvk = sipFV(P, r, nk);
      const investedk = P * nk;
      const gainsk = fvk - investedk;
      rows.push({
        year: k,
        primary: fvk,
        segments: [
          { value: investedk, className: "bg-ink", legendClassName: "bg-ink", label: "Invested" },
          { value: Math.max(0, gainsk), className: "bg-brass", legendClassName: "bg-brass", label: "Gains" },
        ],
        columns: [
          { value: formatINR(investedk) },
          { value: formatINR(fvk) },
          { value: formatINR(gainsk), accent: true },
        ],
      });
    }
    return { fv, invested, gains, investedPct, gainsPct, rows };
  }, [monthly, rate, years]);

  return (
    <div>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-6">
          <NumField label="Monthly Investment (₹)" value={monthly} onChange={setMonthly} min={0} testId="sip-monthly" />
          <NumField label="Expected Annual Return (%)" value={rate} onChange={setRate} step={0.1} min={0} max={40} testId="sip-rate" />
          <SliderField label="Investment Duration (years)" value={years} onChange={setYears} min={1} max={40} testId="sip-years" />
        </div>

        <div className="lg:col-span-7 card-paper p-6 md:p-8 ledger-rule" data-testid="sip-result">
          <div className="eyebrow mb-2">Future Value in {years} years</div>
          <div className="text-[38px] md:text-[48px] leading-none text-ink">
            <LedgerNumber value={fv} testId="sip-future-value" />
          </div>

          <div className="mt-8">
            <div className="flex items-center gap-2 eyebrow mb-2">
              <span>Invested vs. Gains</span>
            </div>
            <div className="w-full h-3 rounded-sm overflow-hidden border hairline flex">
              <div style={{ width: `${investedPct}%` }} className="bg-ink transition-all duration-500" />
              <div style={{ width: `${gainsPct}%` }} className="bg-brass transition-all duration-500" />
            </div>
            <div className="flex justify-between mt-2 text-[12px] text-ink-soft">
              <span><span className="inline-block w-2 h-2 bg-ink mr-1.5" /> Invested · {investedPct.toFixed(0)}%</span>
              <span><span className="inline-block w-2 h-2 bg-brass mr-1.5" /> Gains · {gainsPct.toFixed(0)}%</span>
            </div>
          </div>

          <div className="mt-8 divide-y hairline border-t hairline border-b">
            <Row label="Total Invested" value={formatINR(invested)} />
            <Row label="Total Gains" value={formatINR(gains)} accent />
            <Row label="Future Value" value={formatINR(fv)} />
          </div>
        </div>
      </div>

      <YearBreakdown
        rows={rows}
        primaryLabel="Value at end of each year (invested + gains)"
        tableHeaders={["Year", "Amount Invested to Date", "Value at End of Year", "Gains to Date"]}
        testIdPrefix="sip"
      />

      <CalculatorActions
        testIdPrefix="sip"
        withStrategyCta
        buildPayload={() => ({
          type: "sip",
          title: "SIP Calculator",
          one_liner: `an SIP projection — investing ${formatINR(monthly)}/month at ${rate}% expected return for ${years} years`,
          inputs: [
            { label: "Monthly Investment", value: formatINR(monthly) },
            { label: "Expected Annual Return", value: `${rate}%` },
            { label: "Investment Duration", value: `${years} years` },
          ],
          hero: { label: `Future Value in ${years} years`, value: formatINR(fv) },
          summary: [
            { label: "Total Invested", value: formatINR(invested) },
            { label: "Total Gains", value: formatINR(gains), accent: true },
          ],
          row_headers: ["Year", "Invested to Date", "Value at End of Year", "Gains to Date"],
          rows: rows.map((r) => [
            String(r.year), formatINR(r.segments[0].value),
            formatINR(r.primary), formatINR(r.segments[1].value),
          ]),
        })}
      />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between py-3">
      <span className="eyebrow">{label}</span>
      <span className={`num text-[16px] ${accent ? "text-brass-deep" : "text-ink"}`}>{value}</span>
    </div>
  );
}

function NumField({ label, value, onChange, step = 1, min, max, testId }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type="number" value={value} step={step} min={min} max={max}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="fld num" data-testid={testId}
      />
    </label>
  );
}

function SliderField({ label, value, onChange, min, max, testId }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">{label}</span>
        <span className="num text-ink text-[14px]">{value} yrs</span>
      </div>
      <input
        type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#14A5A5]" data-testid={testId}
      />
      <div className="flex justify-between text-[11px] text-ink-soft/80 mt-1 num">
        <span>{min}</span><span>{max}</span>
      </div>
    </label>
  );
}
